/**
 * Registry List Component
 * Displays owner's registries with edit/delete options
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2, Edit2, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import type { CelebrationRegistry } from '@/types/celebrationRegistry.types';

interface RegistryListProps {
  registries: CelebrationRegistry[];
  onEdit: (registry: CelebrationRegistry) => void;
  onDelete: (registryId: number) => Promise<void>;
  isLoading?: boolean;
}

export const RegistryList: React.FC<RegistryListProps> = ({
  registries,
  onEdit,
  onDelete,
  isLoading
}) => {
  const { t } = useTranslation('celebrations');
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteConfirm = async (registryId: number) => {
    try {
      setIsDeleting(true);
      await onDelete(registryId);
      setDeleteConfirmId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  if (registries.length === 0) {
    return (
      <div className="p-8 text-center">
        <p className="text-gray-600 mb-4">
          {t('registry.list.empty')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {registries.map((registry) => (
        <div
          key={registry.id}
          className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          {/* Primary Badge */}
          {registry.is_primary && (
            <div className="flex-shrink-0 pt-1">
              <Star className="h-5 w-5 text-yellow-400 fill-yellow-400" />
            </div>
          )}

          {/* Registry Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 break-words">
              {registry.registry_name}
              {registry.is_primary && (
                <span className="ml-2 text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                  {t('registry.list.primary')}
                </span>
              )}
            </h3>

            {registry.notes && (
              <p className="text-sm text-gray-600 mt-1">{registry.notes}</p>
            )}

            <a
              href={registry.registry_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-blue-600 hover:text-blue-800 break-all"
            >
              {registry.registry_url}
            </a>

            {/* Registry Metadata */}
            <div className="flex gap-4 mt-2 text-xs text-gray-500">
              {registry.registry_platform && (
                <span className="capitalize">{registry.registry_platform.replace('_', ' ')}</span>
              )}
              <span>{t('registry.list.clicks', { count: registry.click_count })}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex-shrink-0 flex gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => onEdit(registry)}
              disabled={isLoading || isDeleting}
              title={t('registry.list.editTitle')}
            >
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setDeleteConfirmId(registry.id)}
              disabled={isLoading || isDeleting}
              title={t('registry.list.deleteTitle')}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ))}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmId !== null} onOpenChange={(open) => {
        if (!open) setDeleteConfirmId(null);
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('registry.list.confirmDeleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('registry.list.confirmDeleteDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogCancel disabled={isDeleting} onClick={() => setDeleteConfirmId(null)}>{t('common:actions.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            onClick={() => {
              if (deleteConfirmId !== null) {
                handleDeleteConfirm(deleteConfirmId);
              }
            }}
            disabled={isDeleting}
            className="bg-red-600 hover:bg-red-700"
          >
            {isDeleting ? t('registry.list.deleting') : t('common:actions.delete')}
          </AlertDialogAction>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
